// src/utilities/audioCombiner.ts
import { IAudioSegment, IPoemSegment } from '../types/WanderComposition';

/**
 * Convert AudioBuffer to WAV Blob
 */
export const audioBufferToWav = async (buffer: AudioBuffer): Promise<Blob> => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length;
  
  const arrayBuffer = new ArrayBuffer(44 + length * numChannels * 2);
  const view = new DataView(arrayBuffer);
  
  // WAV header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length * numChannels * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, length * numChannels * 2, true);
  
  // Convert float samples to 16-bit PCM
  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }
  
  return new Blob([arrayBuffer], { type: 'audio/wav' });
};

/**
 * Load MP3 encoder from CDN
 */
const loadMp3Encoder = async (): Promise<void> => {
  if (typeof window !== 'undefined' && !(window as any).lamejs) {
    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/lamejs@1.2.1/lame.min.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load MP3 encoder'));
      document.head.appendChild(script);
    });
  }
};

/**
 * Decode any browser-recorded audio blob (webm/ogg/wav/…) and re-encode as MP3.
 * MP3 plays on both iOS and Android native players; webm/opus does not on iOS.
 */
export const convertAudioBlobToMp3 = async (audioBlob: Blob): Promise<Blob> => {
  await loadMp3Encoder();

  const AudioContextCtor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error('AudioContext is not available');
  }

  const audioContext = new AudioContextCtor();
  try {
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));

    const samples = audioBuffer.getChannelData(0);
    const pcm = new ArrayBuffer(samples.length * 2);
    const view = new DataView(pcm);
    for (let i = 0; i < samples.length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }

    const mp3encoder = new (window as any).lamejs.Mp3Encoder(1, audioBuffer.sampleRate, 128);
    const mp3Data: BlobPart[] = [];
    const samples16 = new Int16Array(pcm);

    const maxSamples = 1152;
    for (let i = 0; i < samples16.length; i += maxSamples) {
      const sampleChunk = samples16.subarray(i, i + maxSamples);
      const mp3buf = mp3encoder.encodeBuffer(sampleChunk);
      if (mp3buf && mp3buf.length > 0) {
        mp3Data.push(mp3buf);
      }
    }

    const mp3buf = mp3encoder.flush();
    if (mp3buf && mp3buf.length > 0) {
      mp3Data.push(mp3buf);
    }

    return new Blob(mp3Data, { type: 'audio/mpeg' });
  } finally {
    await audioContext.close().catch(() => undefined);
  }
};

/** Alias used by preview / combine flows; same as convertAudioBlobToMp3. */
export const convertWavToMp3 = convertAudioBlobToMp3;

/** True when the MIME type is already MP3 (playable on iOS + Android). */
export function isCrossPlatformVoiceMimeType(mimeType: string): boolean {
  return /audio\/(mpeg|mp3)/i.test(mimeType);
}

/**
 * Ensure a voice recording is MP3 so iOS and Android can play it.
 * Chromium MediaRecorder typically produces webm/opus, which iOS cannot play.
 */
export async function ensureCrossPlatformVoiceFile(file: File): Promise<File> {
  if (isCrossPlatformVoiceMimeType(file.type)) {
    return file;
  }
  const mp3Blob = await convertAudioBlobToMp3(file);
  const baseName = file.name.replace(/\.[^.]+$/, '') || `voice-${Date.now()}`;
  return new File([mp3Blob], `${baseName}.mp3`, {
    type: 'audio/mpeg',
  });
}

/**
 * Combine multiple audio segments into a single audio file
 * Returns a File object ready for upload
 */
export const combineAudioSegments = async (
  segments: Array<{ url: string; trimStart?: number; trimEnd?: number }>
): Promise<File> => {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  
  // Fetch and decode all audio segments
  const audioBuffers: AudioBuffer[] = [];
  for (const segment of segments) {
    const response = await fetch(segment.url);
    const arrayBuffer = await response.arrayBuffer();
    const buffer = await audioContext.decodeAudioData(arrayBuffer);
    
    // Apply trim if specified
    if (segment.trimStart || segment.trimEnd) {
      const startSample = Math.floor((segment.trimStart || 0) * buffer.sampleRate);
      const endSample = segment.trimEnd 
        ? Math.floor(segment.trimEnd * buffer.sampleRate)
        : buffer.length;
      const length = endSample - startSample;
      
      if (length > 0 && startSample < buffer.length) {
        const trimmedBuffer = audioContext.createBuffer(
          buffer.numberOfChannels,
          length,
          buffer.sampleRate
        );
        
        for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
          const sourceData = buffer.getChannelData(channel);
          const destData = trimmedBuffer.getChannelData(channel);
          destData.set(sourceData.subarray(startSample, Math.min(endSample, buffer.length)));
        }
        
        audioBuffers.push(trimmedBuffer);
      }
    } else {
      audioBuffers.push(buffer);
    }
  }
  
  if (audioBuffers.length === 0) {
    throw new Error('No audio segments to combine');
  }
  
  // Determine output parameters
  const sampleRate = Math.max(...audioBuffers.map(b => b.sampleRate));
  const numChannels = Math.max(...audioBuffers.map(b => b.numberOfChannels));
  
  // Calculate total length
  let totalLength = 0;
  for (const buffer of audioBuffers) {
    const resampledLength = Math.floor(buffer.length * (sampleRate / buffer.sampleRate));
    totalLength += resampledLength;
  }
  
  // Create combined buffer
  const combinedBuffer = audioContext.createBuffer(numChannels, totalLength, sampleRate);
  let offset = 0;
  
  // Copy each buffer into the combined buffer
  for (const buffer of audioBuffers) {
    const resampledLength = Math.floor(buffer.length * (sampleRate / buffer.sampleRate));
    
    for (let channel = 0; channel < numChannels; channel++) {
      const sourceData = buffer.getChannelData(Math.min(channel, buffer.numberOfChannels - 1));
      const destData = combinedBuffer.getChannelData(channel);
      
      if (buffer.sampleRate === sampleRate) {
        // Same sample rate, just copy
        destData.set(sourceData, offset);
      } else {
        // Resample (simple linear interpolation)
        const ratio = buffer.sampleRate / sampleRate;
        for (let i = 0; i < resampledLength; i++) {
          const srcIndex = i * ratio;
          const srcIndexFloor = Math.floor(srcIndex);
          const srcIndexCeil = Math.min(srcIndexFloor + 1, sourceData.length - 1);
          const t = srcIndex - srcIndexFloor;
          destData[offset + i] = sourceData[srcIndexFloor] * (1 - t) + sourceData[srcIndexCeil] * t;
        }
      }
    }
    
    offset += resampledLength;
  }
  
  // Convert to WAV then MP3
  const wavBlob = await audioBufferToWav(combinedBuffer);
  const mp3Blob = await convertWavToMp3(wavBlob);
  
  // Create File object
  const fileName = `combined_audio_${Date.now()}.mp3`;
  return new File([mp3Blob], fileName, { type: 'audio/mp3' });
};

/**
 * Build walking audio segments from composition (presencing + poems)
 */
export const buildWalkingAudioSegments = (
  presencing?: IAudioSegment,
  poems?: IPoemSegment[]
): Array<{ url: string; trimStart?: number; trimEnd?: number }> => {
  const segments: Array<{ url: string; trimStart?: number; trimEnd?: number }> = [];
  
  // Add presencing if it exists
  if (presencing?.audioUrl) {
    segments.push({
      url: presencing.audioUrl,
      trimStart: presencing.trimStart,
      trimEnd: presencing.trimEnd || presencing.duration,
    });
  }
  
  // Add poems in order
  if (poems && poems.length > 0) {
    const sortedPoems = [...poems].sort((a, b) => a.orderNo - b.orderNo);
    
    for (const poem of sortedPoems) {
      if (poem.audioUrl) {
        segments.push({
          url: poem.audioUrl,
          trimStart: poem.trimStart,
          trimEnd: poem.trimEnd || poem.duration,
        });
      }
      
      // Add bridge if it exists
      if (poem.bridgeAudio?.audioUrl) {
        segments.push({
          url: poem.bridgeAudio.audioUrl,
          trimStart: poem.bridgeAudio.trimStart,
          trimEnd: poem.bridgeAudio.trimEnd || poem.bridgeAudio.duration,
        });
      }
    }
  }
  
  return segments;
};

